import React from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Paper, styled, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import ManageBox from '../../components/Dashboard/Identity/ManageBox';
import ContactInfoBox from '../../components/Dashboard/Identity/ContactInfoBox';
import IdentityBox from '../../components/Dashboard/Identity/IdentityBox';
import usePublisher from '../../hooks/usePublisher';
import IdentifyNavbar from '../../components/Dashboard/Navbar/IdentifyNavbar';
import { PageLoader } from '../../components/Feedback/Loaders';
import { SPACING } from '../../theme/theme';

// Mirrors Identifies.tsx: the header clearance comes from the app layout, and
// the page's own breathing room is this padding. `theme.spacing(N)` is N pixels
// here (the theme sets spacing to 1), so the old values were 10px/2px.
const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginTop: 0,
  padding: '40px',
  backgroundColor: 'inherit',
  [theme.breakpoints.down('sm')]: {
    padding: `${SPACING.md}px`,
  },
}));

// No padding of its own — PageContainer supplies the page gutter. The old
// `theme.spacing(2)` added a stray 2px that offset this block from the navbar
// and error banner above it.
const ContentWrapper = styled(Paper)({
  width: '100%',
  maxWidth: '1100px',
  minHeight: '400px',
  boxShadow: 'none',
  backgroundColor: 'inherit',
  direction: 'rtl',
  padding: 0,
  boxSizing: 'border-box',
});

const Identify: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const publisherId = Number(id);
  const {
    data: publisher,
    isLoading,
    isError,
    error,
  } = usePublisher(publisherId);

  if (isLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <PageLoader label="در حال بارگذاری اطلاعات هویت" />
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography variant="body1" color="error">
            خطا: {error.message}
          </Typography>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (!publisher) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography variant="body1">داده‌های ناشر یافت نشد.</Typography>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // The type the publisher will have once review is done. It differs from the
  // live `identity_type` only while a conversion to حقوقی is waiting; the
  // forms, labels and the "تغییر هویت" card all follow this one, so a
  // publisher who has already asked to become a company is not offered the
  // personal form — or the conversion — a second time.
  const identity_type =
    publisher.proposed_identity_type || publisher.identity_type;
  const typeChangePending =
    publisher.draft?.type === 'LEGAL' && publisher.identity_type !== 'LEGAL';
  // A LEGAL publisher renders two cards, everyone else three. Split the 12
  // Grid columns between however many are on screen so they come out equal.
  const cardColumns = identity_type === 'LEGAL' ? 6 : 4;

  return (
    <PageContainer>
      <ContentWrapper>
        <IdentifyNavbar
          publisherId={publisherId.toString()}
          identify={identity_type}
          errors={publisher.errors}
        />

        {/* An edit to an approved identity is held for review; the cards
            below keep showing the live identity, which is what the store
            shows. Without this the publisher cannot tell the two apart and
            assumes the save was lost. */}
        {publisher.has_pending_changes && (
          <Alert
            severity="warning"
            sx={{
              marginTop: `${SPACING.md}px`,
              fontFamily: 'IRANYekanXFaNum',
              direction: 'rtl',
            }}
          >
            {typeChangePending
              ? 'درخواست تبدیل هویت شما به حقوقی ثبت شده و در انتظار بررسی است. '
              : 'تغییرات اطلاعات هویتی شما ثبت شده و در انتظار بررسی است. '}
            تا زمان تایید، اطلاعات و مدارک فعلی معتبر می‌مانند و اپلیکیشن‌های
            شما منتشر باقی می‌مانند.
          </Alert>
        )}
        {/* spacing is a pixel value (the theme sets spacing to 1); this was 2px.
            No `width`/`margin` overrides here — MUI implements `spacing` with a
            negative margin, and forcing `margin: 0 auto` cancelled it on the
            right only, which is what pushed the row off the navbar's edges. */}
        {/* `md` is computed, not fixed at 4: the third card is hidden for a
            LEGAL publisher, and three-column items then left two narrow cards
            centred in the row instead of filling it. */}
        <Grid container spacing={SPACING.md} alignItems="stretch">
          <Grid item xs={12} sm={6} md={cardColumns}>
            <ManageBox
              displayName={publisher.display_name || ''}
              nationalId={publisher.national_id || ''}
              nationalIdImage={publisher.national_id_image || ''}
              pendingNationalIdImage={publisher.pending_national_id_image || ''}
              name={publisher.name || ''}
              identityType={identity_type}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={cardColumns}>
            <ContactInfoBox
              postalCode={publisher.postal_code || ''}
              address={publisher.address || ''}
              phone={publisher.phone || ''}
            />
          </Grid>
          {/* The card only offers حقیقی → حقوقی, so it is meaningless once the
              publisher is already legal — it invited a change that the api
              would reject. */}
          {identity_type !== 'LEGAL' && (
            <Grid item xs={12} sm={6} md={cardColumns}>
              <IdentityBox
                status={publisher.status || ''}
                publisherId={publisher.id.toString()}
              />
            </Grid>
          )}
        </Grid>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Identify;
